import { lazy, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { useAppNavigate } from "../hooks/useAppNavigate";
import { useAuthContext } from "../../contexts/AuthContext";
// This barrel is imported statically by the router, so everything it imports
// statically lands in the entry chunk and on every page's critical path.
// Keep county configs (CountyConfigProvider / the lib/cie barrel), the
// neighborhood table and the packet services behind lazy boundaries here;
// scripts/ci/entry-chunk-weight-scan.mjs fails the build when they leak back.
import { LazyCountyConfigProvider } from "../../lib/cie/LazyCountyConfigProvider";
import { supabase } from "../../lib/supabase";
import type { OutcomeSurveyPacketContext } from "../components/OutcomeSurvey";

// ---------------------------------------------------------------------------
// Lazy page component imports — each becomes its own code-split chunk.
// The wrappers themselves are tiny (just hook calls); the heavy page
// components are only fetched when the route renders.
// ---------------------------------------------------------------------------

const AboutPage = lazy(() =>
  import("../components/AboutPage").then((m) => ({ default: m.AboutPage }))
);
const FaqPage = lazy(() =>
  import("../components/FaqPage").then((m) => ({ default: m.FaqPage }))
);
const BlogPage = lazy(() =>
  import("../components/BlogPage").then((m) => ({ default: m.BlogPage }))
);
const BlogPostPage = lazy(() =>
  import("../components/BlogPostPage").then((m) => ({
    default: m.BlogPostPage,
  }))
);
const SuburbLandingPage = lazy(() =>
  import("../components/SuburbLandingPage").then((m) => ({
    default: m.SuburbLandingPage,
  }))
);
const SuburbSEOPage = lazy(() =>
  import("../components/SuburbSEOPage").then((m) => ({
    default: m.SuburbSEOPage,
  }))
);
const DecisionDecider = lazy(() =>
  import("../components/DecisionDecider").then((m) => ({
    default: m.DecisionDecider,
  }))
);
const QRAdmin = lazy(() =>
  import("../components/admin/QRAdmin").then((m) => ({
    default: m.QRAdmin,
  }))
);
const ContactPage = lazy(() =>
  import("../components/ContactPage").then((m) => ({
    default: m.ContactPage,
  }))
);
const HowItWorksPage = lazy(() =>
  import("../components/HowItWorksPage").then((m) => ({
    default: m.HowItWorksPage,
  }))
);
const VsOwnwellPage = lazy(() =>
  import("../components/VsOwnwellPage").then((m) => ({
    default: m.VsOwnwellPage,
  }))
);
const TermsPage = lazy(() =>
  import("../components/LegalDocumentPage").then((m) => ({
    default: m.TermsPage,
  }))
);
const PrivacyPolicyPage = lazy(() =>
  import("../components/LegalDocumentPage").then((m) => ({
    default: m.PrivacyPolicyPage,
  }))
);
const UPLDisclaimerPage = lazy(() =>
  import("../components/LegalDocumentPage").then((m) => ({
    default: m.UPLDisclaimerPage,
  }))
);
const NotFoundPage = lazy(() =>
  import("../components/NotFoundPage").then((m) => ({
    default: m.NotFoundPage,
  }))
);
const ServerErrorPage = lazy(() =>
  import("../components/ServerErrorPage").then((m) => ({
    default: m.ServerErrorPage,
  }))
);
const PasswordResetPage = lazy(() =>
  import("../components/PasswordResetPage").then((m) => ({
    default: m.PasswordResetPage,
  }))
);
const AppealHQ = lazy(() =>
  import("../components/AppealHQ").then((m) => ({ default: m.AppealHQ }))
);
const FilingGuide = lazy(() =>
  import("../components/FilingGuide").then((m) => ({
    default: m.FilingGuide,
  }))
);
const HearingPrepGuide = lazy(() =>
  import("../components/HearingPrepGuide").then((m) => ({
    default: m.HearingPrepGuide,
  }))
);
const AccountSettingsPage = lazy(() =>
  import("../components/AccountSettingsPage").then((m) => ({
    default: m.AccountSettingsPage,
  }))
);
const CaseWorkspacePage = lazy(() =>
  import("../components/CaseWorkspacePage").then((m) => ({ default: m.CaseWorkspacePage }))
);
const HelpCenterPage = lazy(() =>
  import("../components/HelpCenterPage").then((m) => ({
    default: m.HelpCenterPage,
  }))
);
const BillingPage = lazy(() =>
  import("../components/BillingPage").then((m) => ({
    default: m.BillingPage,
  }))
);
const OutcomeSurvey = lazy(() =>
  import("../components/OutcomeSurvey").then((m) => ({
    default: m.OutcomeSurvey,
  }))
);
const HearingMode = lazy(() =>
  import("../components/HearingMode").then((m) => ({
    default: m.HearingMode,
  }))
);
const NeighborhoodLandingGuard = lazy(() =>
  import("../components/NeighborhoodLandingGuard").then((m) => ({
    default: m.NeighborhoodLandingGuard,
  }))
);
const ShellDemoPage = lazy(() =>
  import("../components/ShellDemoPage").then((m) => ({
    default: m.ShellDemoPage,
  }))
);
const HomePage = lazy(() =>
  import("../components/HomePage").then((m) => ({ default: m.HomePage }))
);
const StickyFooter = lazy(() =>
  import("../components/StickyFooter").then((m) => ({
    default: m.StickyFooter,
  }))
);
const LoadingStatesGallery = lazy(() =>
  import("../components/LoadingStatesGallery").then((m) => ({
    default: m.LoadingStatesGallery,
  }))
);
const ToastDemoPage = lazy(() =>
  import("../components/ToastDemoPage").then((m) => ({
    default: m.ToastDemoPage,
  }))
);
const FormDemoPage = lazy(() =>
  import("../components/FormDemoPage").then((m) => ({
    default: m.FormDemoPage,
  }))
);
const PropertyTaxArgumentLibraryPage = lazy(() =>
  import("../components/PropertyTaxArgumentLibraryPage").then((m) => ({
    default: m.PropertyTaxArgumentLibraryPage,
  }))
);
const PropertyTaxArgumentEntryPage = lazy(() =>
  import("../components/PropertyTaxArgumentEntryPage").then((m) => ({
    default: m.PropertyTaxArgumentEntryPage,
  }))
);

// CookieConsentBanner stays eager — it renders on every page in RootLayout
import { CookieConsentBanner } from "../components/CookieConsentBanner";

// ─── Dual-prop pages (onNavigateHome + onNavigate) ─────────────────

export function AboutPageWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return <AboutPage onNavigateHome={navigateHome} onNavigate={appNavigate} />;
}

export function FaqPageWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return <FaqPage onNavigateHome={navigateHome} onNavigate={appNavigate} />;
}

export function BlogPageWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return <BlogPage onNavigateHome={navigateHome} onNavigate={appNavigate} />;
}

export function BlogPostPageWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <BlogPostPage onNavigateHome={navigateHome} onNavigate={appNavigate} />
  );
}

export function SuburbLandingPageWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbLandingPage onNavigateHome={navigateHome} onNavigate={appNavigate} />
  );
}

// ─── Suburb SEO landing pages (answer-first, AI/GEO optimized) ────────

export function KatyTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="katy-tx" />
  );
}

export function SugarLandTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="sugar-land-tx" />
  );
}

export function TheWoodlandsTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="the-woodlands-tx" />
  );
}

export function PearlandTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="pearland-tx" />
  );
}

export function CypressTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="cypress-tx" />
  );
}

// Ring county suburb SEO wrappers
export function ConroeTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="conroe-tx" />
  );
}

export function SpringTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="spring-tx" />
  );
}

export function MissouriCityTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="missouri-city-tx" />
  );
}

export function StaffordTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="stafford-tx" />
  );
}

export function FriendswoodTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="friendswood-tx" />
  );
}

export function LeagueCityTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="league-city-tx" />
  );
}

export function ClearLakeTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="clear-lake-tx" />
  );
}

// DFW market suburb SEO wrappers
export function PlanoTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="plano-tx" />
  );
}

export function FriscoTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="frisco-tx" />
  );
}

export function McKinneyTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="mckinney-tx" />
  );
}

export function AllenTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="allen-tx" />
  );
}

export function FlowerMoundTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="flower-mound-tx" />
  );
}

export function DentonTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="denton-tx" />
  );
}

export function ProsperTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="prosper-tx" />
  );
}

export function SouthlakeTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="southlake-tx" />
  );
}

export function TrophyClubTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="trophy-club-tx" />
  );
}

export function ArgyleTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="argyle-tx" />
  );
}

export function HighlandVillageTXLandingWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <SuburbSEOPage onNavigateHome={navigateHome} onNavigate={appNavigate} suburbSlug="highland-village-tx" />
  );
}

// ─── SEO neighborhood landing pages (dynamic, data-driven) ────────────

/**
 * Dynamic neighborhood landing wrapper — reads :slug from URL params.
 * 404 guard: if slug is not in NEIGHBORHOOD_CONFIGS, redirects to /search.
 */
export function NeighborhoodLandingWrapper() {
  const { slug } = useParams<{ slug: string }>();
  const { appNavigate, navigateHome } = useAppNavigate();

  // Hard blocker: unknown slugs → redirect to search (not a blank/broken page).
  // The slug check lives in a lazy chunk so the neighborhood table stays out
  // of the entry bundle.
  return (
    <NeighborhoodLandingGuard slug={slug}>
      <SuburbLandingPage
        onNavigateHome={navigateHome}
        onNavigate={appNavigate}
        suburbSlug={slug}
      />
    </NeighborhoodLandingGuard>
  );
}

// ─── Property Tax Argument Library (public, governed by editorial gate) ──

/**
 * Hub page at /property-tax-arguments. Renders only published+validated
 * entries via `getPublishedArguments()`; today that set is empty, so the
 * hub shows the "in editorial review" state and links to /search.
 */
export function PropertyTaxArgumentLibraryWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <PropertyTaxArgumentLibraryPage
      onNavigateHome={navigateHome}
      onNavigate={appNavigate}
    />
  );
}

/**
 * Per-entry page at /property-tax-arguments/:slug. The page component
 * itself enforces the editorial gate via `getPublishedArgumentBySlug` —
 * unknown / draft / shape-invalid slugs redirect to the hub.
 */
export function PropertyTaxArgumentEntryWrapper() {
  const { appNavigate, navigateHome } = useAppNavigate();
  return (
    <PropertyTaxArgumentEntryPage
      onNavigateHome={navigateHome}
      onNavigate={appNavigate}
    />
  );
}

// ─── Auth callback (OAuth redirect handler) ─────────────────────────

/**
 * Handles the /auth/callback route after Google OAuth.
 * Supabase JS auto-exchanges the PKCE code via onAuthStateChange.
 * Once auth state resolves we redirect to the stored return URL
 * (or /dashboard as fallback).
 */
export function AuthCallbackWrapper() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuthContext();
  const hasNavigated = useRef(false);

  /** Read and clear the redirect URL from localStorage (once). */
  const consumeRedirectUrl = (): string => {
    // New key (full href)
    const redirect = localStorage.getItem("fp_auth_redirect");
    if (redirect) {
      localStorage.removeItem("fp_auth_redirect");
      // Extract pathname+search from the full URL for navigate()
      try {
        const url = new URL(redirect);
        return `${url.pathname}${url.search}`;
      } catch {
        return redirect; // Already a relative path
      }
    }
    // Legacy key fallback
    const legacy = localStorage.getItem("fp_auth_return_url");
    if (legacy) {
      localStorage.removeItem("fp_auth_return_url");
      return legacy;
    }
    return "/dashboard";
  };

  // Primary: navigate once auth is confirmed
  useEffect(() => {
    if (isLoading || hasNavigated.current) return;
    if (!isAuthenticated) return; // Wait for code exchange to complete

    hasNavigated.current = true;
    navigate(consumeRedirectUrl(), { replace: true });
  }, [isAuthenticated, isLoading, navigate]);

  // Fallback: if auth doesn't confirm within 4 seconds, redirect anyway.
  // Covers edge cases where code exchange fails silently.
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!hasNavigated.current) {
        hasNavigated.current = true;
        navigate(consumeRedirectUrl(), { replace: true });
      }
    }, 4000);
    return () => clearTimeout(timeout);
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <p className="text-fp-slate-600 text-sm font-inter">Completing sign in…</p>
      </div>
    </div>
  );
}

// ─── Decision Decider (reads ?address query param) ──────────────────

export function DecisionDeciderWrapper() {
  return <DecisionDecider />;
}

// ─── QR short code landing page (/a/:code) — Decision Decider ───────

const MailCodeLanding = lazy(() =>
  import("../components/MailCodeLanding").then((m) => ({
    default: m.MailCodeLanding,
  }))
);

export function QRLandingWrapper() {
  return <MailCodeLanding />;
}

// ─── QR Admin (internal tool) ───────────────────────────────────────

export function QRAdminWrapper() {
  return <QRAdmin />;
}

// ─── onNavigate-only pages ─────────────────────────────────────────

export function ContactPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <ContactPage onNavigate={appNavigate} />;
}

export function HowItWorksPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <HowItWorksPage onNavigate={appNavigate} />;
}

export function VsOwnwellPageWrapper() {
  return <VsOwnwellPage />;
}

export function TermsPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <TermsPage onNavigate={appNavigate} />;
}

export function PrivacyPolicyPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <PrivacyPolicyPage onNavigate={appNavigate} />;
}

export function UPLDisclaimerPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <UPLDisclaimerPage onNavigate={appNavigate} />;
}

export function AppealHQWrapper() {
  const { appNavigate } = useAppNavigate();
  return <AppealHQ onNavigate={appNavigate} />;
}

export function FilingGuideWrapper() {
  const { appNavigate } = useAppNavigate();
  return <FilingGuide onNavigate={appNavigate} />;
}

export function HearingPrepGuideWrapper() {
  const [searchParams] = useSearchParams();
  const countyId = searchParams.get("county") ?? "unknown_us";
  return (
    <LazyCountyConfigProvider countyId={countyId}>
      <HearingPrepGuide />
    </LazyCountyConfigProvider>
  );
}

export function AccountSettingsPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <AccountSettingsPage onNavigate={appNavigate} />;
}

export function CaseWorkspacePageWrapper() {
  return <CaseWorkspacePage />;
}

export function HelpCenterPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <HelpCenterPage onNavigate={appNavigate} />;
}

export function BillingPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <BillingPage onNavigate={appNavigate} />;
}

export function LoadingStatesGalleryWrapper() {
  const { appNavigate } = useAppNavigate();
  return <LoadingStatesGallery onNavigate={appNavigate} />;
}

export function ToastDemoPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <ToastDemoPage onNavigate={appNavigate} />;
}

export function FormDemoPageWrapper() {
  const { appNavigate } = useAppNavigate();
  return <FormDemoPage onNavigate={appNavigate} />;
}

// ─── onNavigateHome-only pages ─────────────────────────────────────

export function NotFoundPageWrapper() {
  const { navigateHome, appNavigate } = useAppNavigate();
  return (
    <NotFoundPage
      onNavigateHome={navigateHome}
      onNavigate={appNavigate}
    />
  );
}

export function ServerErrorPageWrapper() {
  const { navigateHome } = useAppNavigate();
  return <ServerErrorPage onNavigateHome={navigateHome} />;
}

export function PasswordResetPageWrapper() {
  const { navigateHome } = useAppNavigate();
  return <PasswordResetPage onNavigateHome={navigateHome} />;
}

export function PasswordResetExpiredWrapper() {
  const { navigateHome } = useAppNavigate();
  return <PasswordResetPage onNavigateHome={navigateHome} isExpiredDemo />;
}

export function OutcomeSurveyWrapper() {
  const { navigate } = useAppNavigate();
  const { user } = useAuthContext();
  const [packetContext, setPacketContext] =
    useState<OutcomeSurveyPacketContext | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // Loaded here rather than at the top of the barrel: supabase-db pulls
        // the packet evidence snapshot contract into whatever imports it.
        const { getUserPackets } = await import("../../services/supabase-db");
        const packets = await getUserPackets(user.id);
        // Find most recent packet that hasn't reported an outcome yet
        const eligible =
          packets.find((p) => !p.outcome && p.status === "complete") ??
          null;

        // If all packets already have outcomes, pass the most recent
        // with existingOutcome so OutcomeSurvey shows "already recorded"
        const target = eligible ?? packets[0];
        if (!target) return;

        // Fetch assessed_value from the property row
        const { data: prop } = await supabase
          .from("properties")
          .select("assessed_value")
          .eq("id", target.propertyId)
          .maybeSingle();

        setPacketContext({
          packetId: target.id,
          userId: user.id,
          assessedValue: Number(prop?.assessed_value) || 0,
          existingOutcome: target.outcome,
        });
      } catch (err) {
        console.warn("[OutcomeSurveyWrapper] failed to load packet context", err);
      }
    })();
  }, [user]);

  return (
    <OutcomeSurvey
      onNavigateHome={() => navigate("/dashboard")}
      packetContext={packetContext}
    />
  );
}

// ─── Special-case wrappers ─────────────────────────────────────────

export function HearingModeWrapper() {
  const { navigate } = useAppNavigate();
  return <HearingMode onExit={() => navigate("/hearing")} />;
}

export function HomeWrapper() {
  return <HomePage />;
}

export function CookieConsentBannerWrapper() {
  const { appNavigate } = useAppNavigate();
  return <CookieConsentBanner onNavigate={appNavigate} />;
}

export function ShellDemoWrapper() {
  // The page body reads useCountyConfig(), which lives in the provider module;
  // it is lazy so the CIE registry does not load with this barrel.
  return <ShellDemoPage />;
}
